import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as RetryIcon,
  Delete as ClearIcon,
  Speed as QueueIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  HourglassEmpty as WaitingIcon,
  Sync as ActiveIcon,
  Storage as ReindexIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { PageHeader } from '../components/common';
import { adminApi } from '../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8888';

const Queues = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [queues, setQueues] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [reindexDialogOpen, setReindexDialogOpen] = useState(false);
  const [reindexing, setReindexing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [queueStats, health] = await Promise.all([
        adminApi.getQueueStats(),
        adminApi.getSystemHealth(),
      ]);
      setQueues(queueStats.data.queues || []);
      setSystemHealth(health.data);
    } catch (err) {
      console.error('Failed to fetch queue stats:', err);
      // Show empty state on error - no mock data
      setQueues([]);
      setSystemHealth(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRetryFailed = async (queueName) => {
    try {
      await adminApi.retryFailedJobs(queueName);
      fetchData();
    } catch (err) {
      console.error('Failed to retry jobs:', err);
    }
  };

  const handleClearCompleted = async (queueName) => {
    try {
      await adminApi.clearCompletedJobs(queueName);
      fetchData();
    } catch (err) {
      console.error('Failed to clear completed jobs:', err);
    }
  };

  const handleReindex = async () => {
    try {
      setReindexing(true);
      await adminApi.triggerReindex();
      setReindexDialogOpen(false);
    } catch (err) {
      console.error('Failed to trigger reindex:', err);
    } finally {
      setReindexing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return theme.palette.success.main;
      case 'disconnected':
        return theme.palette.error.main;
      default:
        return theme.palette.warning.main;
    }
  };

  // BullBoard URL (opens in new tab)
  const bullBoardUrl = `${API_BASE_URL}/admin/queues`;

  return (
    <Box>
      <PageHeader
        title="queue management"
        subtitle="monitor and manage background jobs"
        breadcrumbs={[{ label: 'queues' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(bullBoardUrl, '_blank')}
            >
              Full Dashboard
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReindexIcon />}
              onClick={() => setReindexDialogOpen(true)}
            >
              Trigger Reindex
            </Button>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {/* System Health */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                System Health
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(systemHealth?.redis?.status),
                          boxShadow: `0 0 8px ${getStatusColor(systemHealth?.redis?.status)}`,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Redis
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {systemHealth?.redis?.latency}ms latency
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(systemHealth?.elasticsearch?.status),
                          boxShadow: `0 0 8px ${getStatusColor(systemHealth?.elasticsearch?.status)}`,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Elasticsearch
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {systemHealth?.elasticsearch?.latency}ms latency
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(systemHealth?.supabase?.status),
                          boxShadow: `0 0 8px ${getStatusColor(systemHealth?.supabase?.status)}`,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Supabase
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {systemHealth?.supabase?.latency}ms latency
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Uptime
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {systemHealth?.uptime}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Queue Cards */}
      <Grid container spacing={3}>
        {loading ? (
          [1, 2].map((i) => (
            <Grid item xs={12} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))
        ) : queues.length === 0 ? (
          <Grid item xs={12}>
            <Alert severity="info">No queues available. Redis may not be connected.</Alert>
          </Grid>
        ) : (
          queues.map((queue) => (
            <Grid item xs={12} key={queue.name}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                      <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QueueIcon sx={{ color: theme.palette.primary.main }} />
                        {queue.displayName || queue.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {queue.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {queue.failed > 0 && (
                        <Tooltip title="Retry failed jobs">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<RetryIcon />}
                            onClick={() => handleRetryFailed(queue.name)}
                          >
                            Retry ({queue.failed})
                          </Button>
                        </Tooltip>
                      )}
                      {queue.completed > 0 && (
                        <Tooltip title="Clear completed jobs">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ClearIcon />}
                            onClick={() => handleClearCompleted(queue.name)}
                          >
                            Clear Completed
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: `${theme.palette.warning.main}10`,
                          textAlign: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                          <WaitingIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            Waiting
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{ color: theme.palette.warning.main }}>
                          {queue.waiting}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: `${theme.palette.info.main}10`,
                          textAlign: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                          <ActiveIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            Active
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{ color: theme.palette.info.main }}>
                          {queue.active}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: `${theme.palette.success.main}10`,
                          textAlign: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                          <CompletedIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            Completed
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{ color: theme.palette.success.main }}>
                          {queue.completed.toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: `${theme.palette.error.main}10`,
                          textAlign: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                          <FailedIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            Failed
                          </Typography>
                        </Box>
                        <Typography variant="h3" sx={{ color: theme.palette.error.main }}>
                          {queue.failed}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Reindex Dialog */}
      <Dialog open={reindexDialogOpen} onClose={() => setReindexDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Trigger Full Reindex</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will reindex all recipes in Elasticsearch. This can take several minutes.
          </Alert>
          <Typography variant="body2">
            A full reindex will:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Fetch all recipes from Supabase" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Recreate the Elasticsearch index" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Re-index all recipe data" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReindexDialogOpen(false)} disabled={reindexing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleReindex}
            disabled={reindexing}
          >
            {reindexing ? 'Reindexing...' : 'Start Reindex'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Queues;
